import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../components/DailyBudgetInvoiceItemsTable/aggridCustom.css";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellClickedEvent, ColDef, CellValueChangedEvent, GetRowIdParams } from "ag-grid-community";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import SyncIcon from "@mui/icons-material/Sync";
import { useNavigate } from "react-router-dom";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import { useGetFavoriteCountsQuery } from "../../services/favorites/favorites";
import {
  useGetItemsQuery,
  useDeleteItemMutation,
  useUpdateItemMutation,
} from "../../services/items/items";
import { ItemAdminType } from "../../types/types";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import { PageContentBox } from "../shared/styledComponents";
import { CATEGORY_LABELS, ITEM_LIST_TEXTS, SNACKBAR_AUTO_HIDE_MS } from "../shared/constants";
import { formatCOP } from "../../utils/format";
import { GREEN, RED } from "../../styles/mui-theme";
import DeleteItemDialog from "./DeleteItemDialog";
import AlegraSyncDialog from "./AlegraSyncDialog";
import useAlegraSync from "../../hooks/useAlegraSync";
import { applyCurationFilter, countByFilter, type CurationFilter } from "./curationFilters";

const TABLE_HEIGHT = "700px";

// 100 fills the 700px viewport several times over, so paging is rare during normal scrolling while
// still capping how many rows AG Grid keeps rendered at once.
const ITEM_GRID_PAGE_SIZE = 100;
const ITEM_GRID_PAGE_SIZES = [50, 100, 200, 500];

// "Pendientes de curar" leads the rest: it is the queue a sync actually produces, and the reason
// this screen exists (EB-509). "Todos" stays first only because it is the default view.
const CURATION_FILTERS: ReadonlyArray<{
  value: CurationFilter;
  label: string;
  tooltip?: string;
}> = [
  { value: "all", label: ITEM_LIST_TEXTS.filterAll },
  {
    value: "pending",
    label: ITEM_LIST_TEXTS.filterPending,
    tooltip: ITEM_LIST_TEXTS.filterPendingTooltip,
  },
  { value: "inactive", label: ITEM_LIST_TEXTS.filterInactive },
  {
    value: "recent",
    label: ITEM_LIST_TEXTS.filterRecent,
    tooltip: ITEM_LIST_TEXTS.filterRecentTooltip,
  },
];

const ItemListPage: React.FC = () => {
  useUpdateAuthToken();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<ItemAdminType> | null>(null);

  const { data: items, isFetching } = useGetItemsQuery();
  // Admin-only and a whole-table read, so it is deliberately separate from the catalog query: if it
  // 403s or fails, `favoriteCounts` is undefined and the column renders 0 rather than the grid
  // breaking (EB-523).
  const { data: favoriteCounts } = useGetFavoriteCountsQuery();
  const [deleteItem, { isLoading: isDeleting }] = useDeleteItemMutation();
  const [updateItem] = useUpdateItemMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemAdminType | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState("");
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [curationFilter, setCurationFilter] = useState<CurationFilter>("all");

  // The run lives in the hook rather than the dialog: it continues in a Lambda whether or not the
  // dialog is open, and the toolbar button needs to know one is in flight to disable itself.
  const sync = useAlegraSync();

  // Counted over the whole catalog, not the filtered view, so the numbers stay comparable as you
  // move between filters — they are the "is the backlog shrinking?" metric (EB-509).
  const counts = useMemo(() => countByFilter(items), [items]);

  // The shallow copy is load-bearing, despite EB-510 listing its removal as scope. AG Grid writes
  // the new value straight into the row object on an inline edit, and `items` is the RTK Query
  // cache, which `configureStore`'s default middleware freezes in development — editing a cell
  // would throw on a read-only property, and in production it would silently mutate cached state
  // that nothing invalidated. EB-521's projection is what made this cheap: 12 shallow fields per
  // row instead of the full ~2 KB document.
  const rowData = useMemo(
    () =>
      isFetching || !items
        ? undefined
        : applyCurationFilter(items, curationFilter).map((i) => ({
            ...i,
            // Joined in here rather than fetched per row: the counts arrive as one object keyed by
            // item id, and AG Grid sorts and filters on row fields, not on external lookups.
            favorites_count: (i.id && favoriteCounts?.[i.id]) || 0,
          })),
    [isFetching, items, curationFilter, favoriteCounts],
  );

  // Identity, not array position. Without it AG Grid rebinds every row whenever `rowData` changes
  // — and it changes on each save, since RTK Query refetches — which throws away the grid's own
  // per-row state. With 2,133 rows that is the difference between repainting one row and all of
  // them.
  const getRowId = useCallback((params: GetRowIdParams<ItemAdminType>) => params.data.id ?? "", []);

  const handleSaveRow = useCallback(
    async (item: ItemAdminType) => {
      const itemId = item.id;
      if (!itemId) return;
      try {
        await updateItem({
          id: itemId,
          patch: {
            name: item.name,
            price: Number(item.price),
            available_quantity: Number(item.available_quantity),
            discount: Number(item.discount),
            is_active: item.is_active,
            tipo_producto: item.tipo_producto || undefined,
            rating: item.rating != null ? Number(item.rating) : undefined,
            marca: item.marca || undefined,
            modelo: item.modelo || undefined,
            condicion: item.condicion || undefined,
          },
        }).unwrap();
        setEditedRows((prev) => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
        setSuccessMsg(ITEM_LIST_TEXTS.saveSuccess);
      } catch {
        setErrorMsg(ITEM_LIST_TEXTS.saveError);
      }
    },
    [updateItem],
  );

  const columnDefs: ColDef<ItemAdminType>[] = useMemo(
    () => [
      {
        headerName: ITEM_LIST_TEXTS.colId,
        field: "id",
        filter: true,
        width: 200,
        cellStyle: { cursor: "pointer", color: GREEN, textDecoration: "underline" },
      },
      {
        headerName: ITEM_LIST_TEXTS.colName,
        field: "name",
        filter: true,
        flex: 1,
        editable: true,
      },
      {
        headerName: ITEM_LIST_TEXTS.colTipo,
        field: "tipo_producto",
        width: 160,
        filter: true,
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["", ...Object.keys(CATEGORY_LABELS)] },
        valueFormatter: (params) => CATEGORY_LABELS[params.value] ?? params.value ?? "",
      },
      {
        headerName: ITEM_LIST_TEXTS.colPrice,
        field: "price",
        width: 150,
        editable: true,
        valueFormatter: (params) => formatCOP(params.value),
      },
      {
        headerName: ITEM_LIST_TEXTS.colQuantity,
        field: "available_quantity",
        width: 120,
        editable: true,
      },
      {
        headerName: ITEM_LIST_TEXTS.colActive,
        field: "is_active",
        width: 100,
        editable: true,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
        cellStyle: (params) => ({
          color: params.value ? GREEN : RED,
          fontWeight: "bold",
        }),
      },
      {
        headerName: ITEM_LIST_TEXTS.colDiscount,
        field: "discount",
        width: 120,
        editable: true,
      },
      {
        headerName: ITEM_LIST_TEXTS.colRating,
        field: "rating",
        width: 100,
        editable: true,
        valueFormatter: (params) => (params.value != null ? `${params.value} ★` : ""),
      },
      {
        headerName: ITEM_LIST_TEXTS.colMarca,
        field: "marca",
        width: 140,
        filter: true,
        editable: true,
      },
      {
        headerName: ITEM_LIST_TEXTS.colModelo,
        field: "modelo",
        width: 140,
        filter: true,
        editable: true,
      },
      {
        headerName: ITEM_LIST_TEXTS.colCondicion,
        field: "condicion",
        width: 120,
        filter: true,
        editable: true,
      },
      {
        headerName: ITEM_LIST_TEXTS.colFavoritos,
        // Read-only: a favourite belongs to a shopper, and an admin editing the count would be
        // editing a tally of other people's actions (EB-523).
        field: "favorites_count",
        width: 120,
        editable: false,
        sortable: true,
      },
      {
        headerName: ITEM_LIST_TEXTS.colFuente,
        // Read-only on purpose (EB-524): the sync owns this field, and an editable provenance is a
        // provenance that can be falsified. Filterable, because "show me everything from Alegra" is
        // the question an admin actually asks of it — but note it is NOT the curation queue: 2,011
        // rows carry `["alegra"]` while only 1,837 were sync-created, which is why "Pendientes de
        // curar" keys on the `alg-` id prefix instead.
        field: "fuente",
        width: 150,
        filter: true,
        editable: false,
        valueFormatter: (params) =>
          Array.isArray(params.value) ? params.value.join(", ") : (params.value ?? ""),
      },
      {
        headerName: ITEM_LIST_TEXTS.colRelacionados,
        // The count is computed server-side (EB-521): the grid never rendered anything but the
        // length, and shipping the relation arrays cost 32% of the response.
        field: "productos_relacionados_count",
        width: 120,
        valueFormatter: (params) => String(params.value ?? 0),
      },
      {
        headerName: ITEM_LIST_TEXTS.colActions,
        width: 120,
        pinned: "right",
        sortable: false,
        filter: false,
        cellRenderer: (params: { data: ItemAdminType }) => (
          <>
            <IconButton
              size="small"
              color="success"
              data-action="save"
              disabled={!params.data.id || !editedRows.has(params.data.id)}
              onClick={(e) => {
                e.stopPropagation();
                void handleSaveRow(params.data);
              }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              data-action="delete"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(params.data);
                setDialogOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ],
    [editedRows, handleSaveRow],
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent<ItemAdminType>) => {
      if (event.colDef.field === "id" && event.data?.id) {
        navigate(`/item-maintenance/${event.data.id}`);
      }
    },
    [navigate],
  );

  const onCellValueChanged = useCallback((event: CellValueChangedEvent<ItemAdminType>) => {
    const id = event.data?.id;
    if (id) {
      setEditedRows((prev) => new Set(prev).add(id));
    }
  }, []);

  const handleConfirmDelete = async () => {
    if (!selectedItem?.id) return;
    try {
      await deleteItem(selectedItem.id).unwrap();
      setSuccessMsg(ITEM_LIST_TEXTS.deleteSuccess);
    } catch {
      setErrorMsg(ITEM_LIST_TEXTS.deleteError);
    } finally {
      setDialogOpen(false);
      setSelectedItem(null);
    }
  };

  return (
    <PageLayout fullWidth showSearch={false}>
      <PageContentBox>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {ITEM_LIST_TEXTS.pageTitle}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5 }} /> }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              // Disabled while a run is in flight, and until the bearer token has landed — both
              // sync routes sit behind the custom authorizer.
              disabled={sync.isRunning || sync.isStarting || !sync.isAuthReady}
              onClick={() => setSyncDialogOpen(true)}
            >
              {ITEM_LIST_TEXTS.syncButton}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/item-maintenance/new")}
            >
              {ITEM_LIST_TEXTS.createButton}
            </Button>
          </Box>
        </Box>

        {/* The curation queue made visible. Counts are of the whole catalog, so they stay
            comparable as you switch filters. */}
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={2}>
          {CURATION_FILTERS.map(({ value, label, tooltip }) => {
            const chip = (
              <Chip
                key={value}
                label={`${label} (${ITEM_LIST_TEXTS.filterCount(counts[value])})`}
                color={curationFilter === value ? "primary" : "default"}
                variant={curationFilter === value ? "filled" : "outlined"}
                onClick={() => setCurationFilter(value)}
                data-testid={`curation-filter-${value}`}
              />
            );

            return tooltip ? (
              <Tooltip key={value} title={tooltip}>
                <span>{chip}</span>
              </Tooltip>
            ) : (
              chip
            );
          })}
        </Box>

        <div className="ag-theme-quartz" style={{ width: "100%", height: TABLE_HEIGHT }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            getRowId={getRowId}
            quickFilterText={searchText}
            // Paging the 2,133 rows the sync produced. `quickFilterText` and the column filters
            // still run across the whole set — AG Grid pages what survives filtering, so searching
            // never gets confined to the current page.
            pagination
            paginationPageSize={ITEM_GRID_PAGE_SIZE}
            paginationPageSizeSelector={ITEM_GRID_PAGE_SIZES}
            overlayLoadingTemplate={"<span>Cargando items...</span>"}
            overlayNoRowsTemplate={"<span>No hay items disponibles.</span>"}
            onCellClicked={onCellClicked}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </PageContentBox>

      <DeleteItemDialog
        open={dialogOpen}
        itemName={selectedItem?.name ?? ""}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedItem(null);
        }}
      />

      <AlegraSyncDialog
        open={syncDialogOpen}
        itemCount={items?.length ?? 0}
        job={sync.job}
        isStarting={sync.isStarting}
        isRunning={sync.isRunning}
        isFinished={sync.isFinished}
        error={sync.error}
        onConfirm={() => void sync.start()}
        onClose={() => {
          setSyncDialogOpen(false);
          // Only clear a finished run. Closing mid-run keeps the job so reopening resumes the
          // progress view instead of offering to start a second sync the lock would refuse.
          if (sync.isFinished) sync.reset();
        }}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setSuccessMsg("")}
      >
        <Alert severity="success" onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default ItemListPage;
